import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import Head from 'next/head'
import { showSimulation } from 'store/simulation/actions/showSimulation'
import { setMarketingParamsInStore } from 'utils/customerFlow'
import { marketingParams } from './constants'
import { trackEvent } from 'utils/eventTracking'
import { useRouter } from 'next/router'
import { MiniGeneratorsProps as Props } from './types'
import { Section } from 'types/faq'
import FaqSection from 'components/faq/faq-section/FaqSection'
import styles from './MiniGenerators.module.scss'
import Button from 'components/button/Button'
import { Check } from './assets'
import parse from 'html-react-parser'
import Image from 'next/image'
import classNames from 'classnames'
import { faqCategoriesSlugs } from 'constants/faq-categories'
import heroImage from '../../../public/images/features/mini-generators/hero.jpg'
import { TITLE_POSTFIX } from 'constants/seo'

const MiniGenerators: FC<Props> = ({ sections, questions }) => {
  // REDUX STORE
  const dispatch = useDispatch()

  // Next Router
  const { locale } = useRouter()

  // i18n
  const { t } = useTranslation('miniGenerators')

  // State
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null)

  // Filtered sections
  const filteredSections = sections.filter((section: Section) => faqCategoriesSlugs.mini_generators[locale].includes(section.slug))

  /**
   * Handles the click event on the CTA buttons
   */
  const handleCtaClick = () => {
    dispatch(showSimulation())
    setMarketingParamsInStore(marketingParams)

    // Track 'Simulation Started' event @Semetis
    trackEvent('simulation_started')
  }

  return (
    <>
      {/* SEO */}
      <Head>
        <title>
          {t('persuasion.who.title')} {TITLE_POSTFIX}
        </title>
        <meta property="og:title" content={`${t('persuasion.who.title')} ${TITLE_POSTFIX}`} key="title" />
      </Head>

      {/* HERO */}
      <section className={classNames('section', styles.hero)}>
        <div className="container has-bg-image">
          <div className={styles.welcome}>
            <h1 className={styles.title}>
              {parse(
                t(
                  'hero.title',
                  <>
                    <p>
                      <span>
                        Word <span className="is-green">mini-opwekker</span>
                        <br />
                      </span>
                    </p>
                    <p>
                      <span>
                        en werk mee aan de toekomst
                        <br />
                      </span>
                    </p>
                    <p>
                      <span>
                        van <span className="is-green">stroomdelen</span>
                      </span>
                    </p>
                  </>
                )
              )}
            </h1>

            <div className={classNames(styles.subtitle, 'is-flex-row', 'margin-top-m')}>
              <div className="margin-right-s">
                <Check />
              </div>

              <p className="is-large margin-bottom-none margin-top-none">
                {parse(
                  t(
                    'hero.check',
                    'Vanaf 2021 ontvang je een vergoeding voor je eigen geproduceerde elektriciteit die je terug in het net injecteert*'
                  )
                )}
              </p>
            </div>

            <Button onClick={() => handleCtaClick()} extraClasses="margin-top-l margin-bottom-s" isFullWidthMobile withGradient>
              {t('hero.button', 'Bereken jouw prijs')}
            </Button>

            <p className="is-white margin-bottom-none margin-top-none">
              {t('hero.small', '*Enkel geldig in Vlaanderen, voor installaties geplaatst vanaf 2021 of installaties ouder dan 15 jaar.')}
            </p>
          </div>
        </div>

        <Image priority src={heroImage} layout="fill" objectFit="cover" alt="Mini Generators Hero" placeholder="blur" />
      </section>

      {/* PERSUASION */}
      <section className="section">
        <div className="container has-text-centered ">
          <div className="is-flex-row is-flex-column-mobile">
            <div className={styles['is-half-width']}>
              <h3 className={classNames('title', styles.title)}>{t('persuasion.who.title', 'Mini-opwekkers, wie zijn dat?')}</h3>
              <p>
                {parse(
                  t(
                    'persuasion.who.description',
                    <>
                      Via Bolt kan je jouw energie rechtstreeks kopen van <strong>lokale, groene opwekkers.</strong> Zij zijn de sterren van
                      hetverhaal. Dankzij hun weet elke Bolt-klant waar zijn energie vandaan komt en waar hun geld naartoe gaat. Nu de
                      regelgeving rond zonne-energie bij de start van 2021 verandert, kan je ook{' '}
                      <strong>als particulier jouw energie ter beschikking stellen </strong>, én ervoor vergoed worden.
                    </>
                  )
                )}
              </p>
            </div>

            <div className={styles['is-half-width']}>
              <h3 className={classNames('title', styles.title)}>
                {t('persuasion.solar.title', 'Dus, heb jij zonnepanelen op je dak liggen?')}
              </h3>
              <p>
                {parse(
                  t(
                    'persuasion.solar.description',
                    <>
                      Word dan nu <strong>zélf mini-opwekker.</strong> Jouw krachten worden gebundeld met andere mini-opwekkers, waardoor
                      jij niet alleen je eigen gezin maar ook die van buurman Floris, nonkel Jos en tante Fien en beste vriendin Elfi kan
                      voorzien van groene, lokale energie. <strong>Vanaf nu speel jij de hoofdrol in je eigen energieverhaal!</strong>
                    </>
                  )
                )}
              </p>
            </div>
          </div>

          <Button onClick={() => handleCtaClick()} extraClasses="margin-top-l margin-bottom-s" isFullWidthMobile withGradient>
            {t('persuasion.contact', 'Bereken jouw prijs')}
          </Button>

          <div className={styles.map}>
            <Image src={`/images/features/mini-generators/map_${locale}.jpg`} width={1920} height={1080} alt="Mini generators map" />
          </div>
        </div>
      </section>

      {/* AUDITIONS */}
      <section className={classNames('section', 'is-green', styles.auditions)}>
        <div className="container">
          <h3 className={classNames('title', styles.title, 'margin-bottom-m')}>
            {t('auditions.title', 'Auditieronde, kom jij in aanmerking voor je rol als mini-opwekker?')}
          </h3>

          <div className={classNames(styles['is-flex-row'], 'is-flex-row', 'padding-x-xl', 'margin-bottom-l')}>
            <div className="margin-right-m">
              <Check />
            </div>
            <p className="margin-top-none">
              {parse(
                t(
                  'auditions.first',
                  <>
                    <div className="is-large">Je zonnepanelen worden in 2021 (of later) geïnstalleerd</div>
                    Denk je er al langer over om zonnepanelen te plaatsen? Dan is dit <strong>hét moment.</strong> Je krijgt automatisch een
                    digitale meter en zal in het <strong>nieuwe systeem van injectietarief</strong> terechtkomen. In dit systeem betaal je
                    enerzijds voor de hoeveelheid elektriciteit die je afneemt en ontvang je anderzijds een injectietarief voor de
                    hoeveelheid elektriciteit die je in het net ‘injecteert’.
                  </>
                )
              )}
            </p>
          </div>

          <div className={classNames(styles['is-flex-row'], 'is-flex-row', 'padding-x-xl')}>
            <div className="margin-right-m">
              <Check />
            </div>
            <p className="margin-top-none">
              {parse(
                t(
                  'auditions.second',
                  <>
                    <div className="is-large">Je installatie is minstens 15 jaar oud in 2021</div>
                    Zodra jouw zonnepaneleninstallatie 15 kaarsjes uitblaast, zal deze door Fluvius{' '}
                    <strong>automatisch op het systeem met injectietarief</strong> met digitale meter worden gezet.
                  </>
                )
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Injection */}
      <section className="section">
        <div className="container">
          <h3 className={classNames('title', styles.title, 'has-text-centered')}>{t('injection.title', 'Het injectietarief in beeld')}</h3>

          <div className={styles.injection}>
            <Image
              src={`/images/features/mini-generators/injection_${locale}.jpg`}
              width={2048}
              height={1236}
              alt="Injectietarief in beeld"
            />
          </div>
        </div>
      </section>

      {/* EXPLANATION */}
      <section className="section is-green">
        <div className="container has-text-centered">
          <div className="is-flex-row is-flex-column-mobile">
            <div className={styles['is-half-width']}>
              <h3 className={classNames('title', styles.title)}>
                {t('explanation.solarOld.title', 'Mijn zonnepaneleninstallatie is nog geen 15 jaar oud, wat nu?')}
              </h3>
              <p className="padding-x-m">
                {parse(
                  t(
                    'explanation.solarOld.description',
                    <>
                      Heb je een digitale meter en zonnepanelen die nog niet 15 jaar oud zijn? Ook jij kan{' '}
                      <strong>vrijwillig overschakelen op het nieuwe systeem en mini-opwekker worden.</strong> Dien hiervoor je aanvraag in
                      bij Fluvius. <br />
                      <br />
                      Doe je dit niet, dan blijf je nog in het systeem van de terugdraaiende teller. In dit systeem wordt je eigen opgewekte
                      energie die je niet gebruikt, teruggestuurd naar het net waardoor je meter terugdraait. Voor je energie betaal je je
                      totale verbruik min de energie die je zelf hebt geproduceerd. Wel betaal je een prosumententarief aan de netbeheerder.
                    </>
                  )
                )}
              </p>
            </div>

            <div className={styles['is-half-width']}>
              <h3 className={classNames('title', styles.title)}>
                {parse(
                  t(
                    'explanation.stageFright.title',
                    <>
                      Plankenkoorts? <br />
                      Dat is normaal.
                    </>
                  )
                )}
              </h3>
              <p className="padding-x-m">
                {parse(
                  t(
                    'explanation.stageFright.description',
                    <>
                      <strong>Welk systeem voor jou voordelig zal uitdraaien</strong>, hangt af van je verbruiksprofiel, namelijk hoe hoog
                      je zelfverbruik is. <br />
                      <br />
                      Bolt zijn stokpaardje is <strong>transparantie</strong>. In de meeste gevallen (dus niet voor iedereen!) is behoud van
                      het principe van de terugdraai- ende teller financieel voordelig. <br />
                      <br />
                      Maar met het nieuwe systeem van het injectietarief werk je als <strong>mini-opwekker</strong> wel mee aan een{' '}
                      <strong>groene transitie</strong> en aan een <strong>toekomst van het stroomdelen</strong>. Concreet? Stroom
                      uitwisselen met en in je buurt, of via de digitale meter nauwkeurig je verbruik opvolgen. Je wasmachine dat
                      automatisch opspringt bij overvloedig zonlicht, dat is toch super?
                    </>
                  )
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className={classNames('section', styles.quote)}>
        <div className="container has-text-centered">
          <div className="is-flex-row is-flex-column-mobile">
            <div className={styles['is-half-width']}>
              <h3 className={classNames('title', styles.title)}>
                {parse(
                  t(
                    'quote.title',
                    <>
                      Meet Marie-Josee. <br />
                      Marie-Josee is mini-opwekker bij Bolt.
                    </>
                  )
                )}
              </h3>
              <p className="is-2-xl">
                “
                {t(
                  'quote.text',
                  'Ik vind het wel leuk dat ik nu via Bolt een gezicht kan plakken op de mensen die mijn elektriciteit gaan gebruiken. Goed dat het niet anoniem opgaat in de massa.'
                )}
                ”
              </p>
              <p>
                {parse(
                  t(
                    'quote.who',
                    <>
                      <strong>Marie-Josee</strong>, mini-opwekker bij Bolt
                    </>
                  )
                )}
              </p>
            </div>
            <div className={styles['is-half-width']}>
              <div className={styles['quote-image']}>
                <Image src="/images/features/mini-generators/quote_image.png" width={960} height={960} alt="Quote" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section is-dark">
        <div className="container">
          <ul>
            {filteredSections.map((section: Section) => (
              <FaqSection
                key={section.id}
                section={section}
                questions={questions}
                selectedQuestionId={selectedQuestionId}
                setSelectedQuestionId={setSelectedQuestionId}
              />
            ))}
          </ul>
        </div>
      </section>
    </>
  )
}

export default MiniGenerators
